import { type GlobalStorageFile } from 'hb-react/shared/hooks/useGlobalStorageV2/types';
import {
  findImagesInContent,
  getGSFileIdentifiersFromContent,
} from 'hb-react/shared/hooks/useGlobalStorageV2/utils';

export const getContentImagesFilesUpdater = (
  initialContent: string,
  currentContent: string,
) => {
  const currentIdentifiers = getGSFileIdentifiersFromContent(currentContent);

  return (prevFiles: Array<GlobalStorageFile>) => {
    let isDirty = false;

    // Update the state of NEW images (i.e. not persisted to the DB yet)
    let newFiles = prevFiles.map((file) => {
      // File is not NEW (if it has an id it's been persisted to the DB already), it will be handled in the code below
      if (file.id) {
        return file;
      }

      const isInContent =
        file.shrineKey &&
        currentIdentifiers.shrineKeys.includes(file.shrineKey);

      // If the existing file status matches, return it directly
      if (isInContent === !file.isRemoved) {
        return file;
      }

      isDirty = true;

      return {
        ...file,
        isRemoved: !isInContent,
      };
    });

    // Update the state of OLD images (i.e. persisted to the DB)
    const initialContentImages = findImagesInContent(initialContent);
    const currentContentImages = findImagesInContent(currentContent);

    initialContentImages.forEach((initialImage) => {
      const isInContent = currentContentImages.some(
        (currentImage) => currentImage.id === initialImage.id,
      );

      // The image is still in the content or was added back
      if (isInContent) {
        // Remove it from the array if it's marked as removed (we don't need to track persisted images unless they are being removed)
        // Leave it there otherwise to avoid unnecessary changes to the files array (which is really a React state)
        if (
          newFiles.some((file) => file.id === initialImage.id && file.isRemoved)
        ) {
          newFiles = newFiles.filter((file) => file.id !== initialImage.id);
          isDirty = true;
        }

        return;
      }

      if (!isInContent) {
        const matchingFile = newFiles.find(
          (file) => file.id === initialImage.id,
        );

        // The image was removed from the content
        // There is no entry for that file in the newFiles, add it
        if (!matchingFile) {
          newFiles.push({ ...initialImage, isRemoved: true });
          isDirty = true;

          return;
        }

        if (!matchingFile.isRemoved) {
          matchingFile.isRemoved = true;
          isDirty = true;
        }
      }
    });

    if (isDirty) {
      return newFiles;
    }

    // This is used as a React state update function. We should always return the same reference if nothing changed
    return prevFiles;
  };
};
